import { groupBy, keyBy } from 'lodash';
import { authInstance } from '../..';

const isLocalApi = process.env.REACT_APP_API_DEV === 'true';

const urlExtension = '/api/';
const apiProdUrl = `${window.location.protocol}//${window.location.hostname}${urlExtension}`;
const apiDevUrl = 'http://localhost:3010/';
const apiUrl = isLocalApi ? apiDevUrl : apiProdUrl;

async function makeApiRequest(endpoint, options = {}) {
  const fullUrl = endpoint.indexOf(apiUrl) === -1 ? apiUrl + endpoint : endpoint;

  if (endpoint !== 'flags') {
    if (!authInstance?.authenticated) {
      console.error('Keycloak not authenticated!');
      return { error: 'Not authenticated' };
    }
    try {
      await authInstance.updateToken(30);
    } catch (err) {
      console.error('Failed to refresh token:', err);
      return { error: 'Session expired' };
    }
    options = {
      ...options,
      headers: {
        accept: 'application/json',
        authorization: `Bearer ${authInstance.token}`,
        ...options.headers,
      },
    };
  }

  try {
    const response = await fetch(fullUrl, options);
    const text = await response.text();

    let json;
    try {
      json = text ? JSON.parse(text) : {};
    } catch {
      return {
        error: !response.ok
          ? response.statusText || `Server error (${response.status})`
          : `Invalid JSON response from ${endpoint}`,
      };
    }

    if (!response.ok) {
      if (response.status === 401) {
        console.error('Unauthorized access - 401');
        return { error: 'Unauthorized' };
      }
      return { error: json.error || json.message || response.statusText };
    }
    return { response: json };
  } catch (err) {
    return { error: err.message || `Something bad happened trying to fetch ${endpoint}` };
  }
}

export async function callApi(endpoint, params = {}) {
  const queryString = new URLSearchParams(params);
  const urlWithParams = Object.keys(params).length > 0 ? `${endpoint}?${queryString}` : endpoint;
  return makeApiRequest(urlWithParams, { method: 'GET' });
}

export async function postToApi(endpoint, data) {
  return makeApiRequest(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: data && JSON.stringify(data),
  });
}

export async function patchToApi(endpoint, data) {
  return makeApiRequest(endpoint, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: data && JSON.stringify(data),
  });
}

export async function deleteFromApi(endpoint) {
  return makeApiRequest(endpoint, { method: 'DELETE' });
}

export const getCausesAndConsequences = ({ causes = [], consequences = [] }) => ({
  causes,
  consequences,
});

const mapObjectRole = (objectRole) => {
  switch (objectRole) {
    case 'Mass:Gas:Gas':
      return 'Gas';
    case 'Mass:Liquid:Seawater':
      return 'Water';
    case 'Mass:Liquid:Oil':
      return 'Oil';
    case 'Energy:Electric Power':
      return 'Utility';
    default:
      return '';
  }
};

export const normalizeModel = (model = {}) => {
  const modelConvertedToArray = Object.entries(model)
    .filter(([, { type }]) => type !== 'relation') // this filters out all the relations from the model
    .map(
      ([
        functionId,
        {
          label,
          description,
          suppressed,
          limits,
          sensor,
          concept,
          ttl,
          labels,
          id,
          parent,
          type,
          counteractions,
          actuatorTag,
          groups,
          objectRole,
          category,
        },
      ]) => {
        return {
          functionId,
          label,
          description,
          suppressed,
          limits,
          sensor,
          concept,
          labels,
          ttl,
          id,
          parent,
          type,
          counteractions,
          actuatorTag,
          groups,
          objectRole: mapObjectRole(objectRole),
          category,
        };
      }
    );
  return keyBy(modelConvertedToArray, 'functionId');
};

export const normalizeEquipmentConfig = (equipmentConfig = {}) => {
  return groupBy(equipmentConfig, ({ equipmentId }) => equipmentId);
};
